/**
 * One consistent Sentry capture for errors that used to be swallowed (#479).
 *
 * The audit found catch blocks across checkout, auth and admin that only
 * console.error'd, toasted, or set local state, so real failures never
 * reached monitoring. Call `reportError` from those sites (keep the toast or
 * message the user already sees) and `reportAuthError` where Firebase Auth
 * errors are mapped to friendly copy, so user-caused codes stay quiet.
 */
import * as Sentry from '@sentry/react';

/**
 * Firebase Auth codes the user causes themselves. They are feedback, not
 * defects, and must not page anyone.
 */
const USER_CAUSED_AUTH_CODES = new Set([
  'auth/invalid-credential',
  'auth/wrong-password',
  'auth/user-not-found',
  'auth/invalid-email',
  'auth/missing-email',
  'auth/missing-password',
  'auth/too-many-requests',
  'auth/user-disabled',
  'auth/weak-password',
  'auth/email-already-in-use',
  'auth/requires-recent-login',
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
]);

/** Transient conditions worth seeing, but at 'warning' rather than 'error'. */
const TRANSIENT_AUTH_CODES = new Set(['auth/network-request-failed']);

/**
 * @param {string | undefined} code - Firebase Auth error code
 * @returns {boolean} true when the failure is the user's to fix
 */
export const isExpectedAuthError = (code) =>
  typeof code === 'string' && USER_CAUSED_AUTH_CODES.has(code);

/**
 * Capture an error with component/action tags. Never throws.
 *
 * @param {unknown} error
 * @param {{component: string, action: string, level?: 'error'|'warning'|'info', extra?: object, tags?: object}} context
 */
export function reportError(
  error,
  { component, action, level = 'error', extra, tags },
) {
  try {
    const isError = error instanceof Error;
    const captured = isError ? error : new Error(String(error));
    const mergedExtra = isError
      ? extra
      : { ...(extra || {}), originalValue: error };

    Sentry.captureException(captured, {
      tags: { component, action, ...(tags || {}) },
      level,
      ...(mergedExtra ? { extra: mergedExtra } : {}),
    });
  } catch {
    // Monitoring must never break the feature it is watching.
  }
}

/**
 * Capture a Firebase Auth failure unless the user caused it. Network failures
 * are reported at 'warning'; everything else unrecognised at 'error'.
 *
 * @param {unknown} error - typically a FirebaseError with a `code`
 * @param {{component: string, action: string, extra?: object}} context
 */
export function reportAuthError(error, { component, action, extra }) {
  const code = error && typeof error.code === 'string' ? error.code : undefined;
  if (isExpectedAuthError(code)) return;

  reportError(error, {
    component,
    action,
    level: code && TRANSIENT_AUTH_CODES.has(code) ? 'warning' : 'error',
    extra,
    // Tagged separately so Sentry can group by code.
    tags: { authCode: code || 'none' },
  });
}

export default reportError;
